import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { CalendarClock, Loader2, AlertTriangle, Check } from 'lucide-react';
import AppHeader from '../components/AppHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const useAuth = () => useContext(AuthContext);

const WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const WORK_HOURS = Array.from({ length: 16 }, (_, i) => 6 + i); // 6..21
const fmtHour = (h) => `${String(h).padStart(2, '0')}:00–${String(h + 1).padStart(2, '0')}:00`;
const todayISO = () => new Date().toISOString().slice(0, 10);

const ScheduleModal = ({ appt, token, onClose }) => {
  const [date, setDate] = useState('');
  const [hour, setHour] = useState(null);
  const [weekly, setWeekly] = useState({});
  const [loadingAvail, setLoadingAvail] = useState(false);
  const [override, setOverride] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      setLoadingAvail(true);
      try {
        const res = await fetch(`${API_URL}/api/admin/veyonika-availability/${appt.provider_id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        setWeekly(data.weekly || {});
      } catch (e) {
        setWeekly({});
      } finally {
        setLoadingAvail(false);
      }
    })();
  }, [appt.provider_id, token]);

  const weekdayKey = date ? WEEKDAYS[new Date(date + 'T00:00:00').getDay()] : null;
  const availableHours = weekdayKey ? (weekly[weekdayKey] || []) : [];
  const noAvailability = !!date && availableHours.length === 0;
  const pickedUnavailable = hour !== null && !availableHours.includes(hour);

  const onPickDate = (v) => { setDate(v); setHour(null); setOverride(false); setError(null); };

  const handleSave = async () => {
    if (hour === null) { setError('Pick a time slot.'); return; }
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/appointments/${appt.id}/schedule`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          scheduled_date: date,
          scheduled_time: `${String(hour).padStart(2, '0')}:00`,
          override,
        }),
      });
      const data = await res.json();
      if (res.status === 409) {
        setError('This time conflicts with the care-giver availability/booking. Enable override to schedule anyway.');
        setSaving(false);
        return;
      }
      if (!res.ok) {
        const d = data.detail;
        throw new Error(typeof d === 'string' ? d : (d?.message || 'Failed to schedule'));
      }
      onClose(true);
    } catch (e) {
      setError(typeof e.message === 'string' ? e.message : 'Failed to schedule');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="schedule-modal">
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[88vh] overflow-y-auto p-6">
        <h2 className="text-lg font-semibold flex items-center gap-2 mb-1">
          <CalendarClock size={20} className="text-purple-600" /> Schedule Therapy Session
        </h2>
        <p className="text-sm text-gray-500 mb-4">
          {appt.grade_label} · Care-giver: <strong>{appt.provider_name || '—'}</strong> · Seeker: {appt.seeker_name || '—'}
        </p>

        {error && <div className="mb-3 p-2 bg-red-50 text-red-700 text-sm rounded" data-testid="schedule-error">{error}</div>}

        <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
        <input
          type="date" value={date} min={todayISO()} onChange={(e) => onPickDate(e.target.value)}
          className="w-full border rounded-lg px-3 py-2 mb-4" data-testid="schedule-date-input"
        />

        {date && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Available slots — {weekdayKey?.charAt(0).toUpperCase() + weekdayKey?.slice(1)}
              </label>
              {loadingAvail && <Loader2 size={14} className="animate-spin text-purple-600" />}
            </div>

            {!loadingAvail && noAvailability && (
              <div className="mb-3 p-2 bg-amber-50 text-amber-800 text-sm rounded flex items-start gap-2" data-testid="no-availability-warning">
                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                <span>No Veyonika availability marked for this day. Enable override below to schedule anyway.</span>
              </div>
            )}

            {/* Available slots (green) */}
            {!loadingAvail && availableHours.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3" data-testid="available-slots">
                {availableHours.map((h) => (
                  <button
                    key={h} onClick={() => setHour(h)} data-testid={`slot-${h}`}
                    className={`text-xs px-2.5 py-1 rounded-full border ${
                      hour === h ? 'bg-purple-600 text-white border-purple-600'
                        : 'bg-green-50 text-green-700 border-green-300 hover:border-green-500'}`}
                  >{fmtHour(h)}</button>
                ))}
              </div>
            )}

            {/* Override controls */}
            <label className="flex items-center gap-2 text-sm text-gray-700 mb-2 cursor-pointer">
              <input type="checkbox" checked={override} onChange={(e) => { setOverride(e.target.checked); if (!e.target.checked && pickedUnavailable) setHour(null); }} data-testid="override-checkbox" />
              Override care-giver availability
            </label>

            {override && (
              <div className="mb-2">
                <p className="text-xs text-amber-700 mb-2 flex items-center gap-1">
                  <AlertTriangle size={14} /> Overriding — you can pick any hour; the care-giver has not marked these as available.
                </p>
                <div className="flex flex-wrap gap-2" data-testid="override-slots">
                  {WORK_HOURS.map((h) => (
                    <button
                      key={h} onClick={() => setHour(h)} data-testid={`override-slot-${h}`}
                      className={`text-xs px-2.5 py-1 rounded-full border ${
                        hour === h ? 'bg-amber-500 text-white border-amber-500'
                          : availableHours.includes(h) ? 'bg-green-50 text-green-700 border-green-300'
                          : 'bg-white text-gray-600 border-gray-200 hover:border-amber-400'}`}
                    >{fmtHour(h)}</button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-5 pt-4 border-t">
          <button onClick={() => onClose(false)} className="px-4 py-2 text-sm border rounded-lg">Cancel</button>
          <button
            onClick={handleSave} disabled={saving || hour === null}
            data-testid="confirm-schedule-btn"
            className="px-4 py-2 text-sm rounded-lg bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
          >
            {saving && <Loader2 size={16} className="animate-spin" />} Confirm Schedule
          </button>
        </div>
      </div>
    </div>
  );
};

export default function AdminSchedulingPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [appts, setAppts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/appointments/to-schedule`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setAppts(data.appointments || []);
    } catch (e) {
      setAppts([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (user?.role !== 'admin' && user?.base_role !== 'admin') { navigate('/'); return; }
    fetchData();
  }, [user, navigate, fetchData]);

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader title="Scheduling" />

      <div className="max-w-4xl mx-auto p-4">
        <p className="text-sm text-gray-500 mb-4">Therapy bookings awaiting a date &amp; time.</p>
        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-purple-600" /></div>
        ) : appts.length === 0 ? (
          <div className="bg-white rounded-xl p-8 text-center text-gray-500" data-testid="no-pending-schedules">
            <Check className="mx-auto mb-2 text-green-500" /> No bookings awaiting scheduling.
          </div>
        ) : (
          <div className="bg-white rounded-xl divide-y" data-testid="to-schedule-list">
            {appts.map((a) => (
              <div key={a.id} className="flex items-center justify-between p-4" data-testid={`schedule-row-${a.id}`}>
                <div>
                  <div className="font-medium text-gray-800">{a.grade_label}</div>
                  <div className="text-sm text-gray-500">
                    Care-giver: {a.provider_name || '—'} · Seeker: {a.seeker_name || '—'}
                    {a.amount ? ` · ₹${a.amount}` : ''}
                  </div>
                </div>
                <button
                  onClick={() => setActive(a)} data-testid={`schedule-btn-${a.id}`}
                  className="px-4 py-2 text-sm rounded-lg bg-purple-600 text-white hover:bg-purple-700"
                >Schedule</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {active && (
        <ScheduleModal
          appt={active} token={token}
          onClose={(saved) => { setActive(null); if (saved) fetchData(); }}
        />
      )}
    </div>
  );
}
